import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  FormLabel,
  Link,
  Typography,
  Box,
  Paper,
  Stack,
  Divider,
  IconButton,
  CircularProgress
} from "@mui/material";
import { 
  Close as CloseIcon,
  CloudUpload as UploadIcon,
  CalendarToday as CalendarIcon,
  Description as FileIcon
} from "@mui/icons-material";
import { useMutation, useQuery } from "react-query";
import { QK } from "../../../base/qk";
import { getDnacprById, updateDnacpr } from "../../../api/services/document";
import { enqueueSnackbar } from "notistack";
import { useParams } from "react-router-dom";

const DnacprEditModal = ({ show, handleClose, data: selectedId, handleEditSubmit, refecthDnacpr }) => {
  const { id: clientId } = useParams();
  const [selectedFile, setSelectedFile] = useState(null);

  const { data: DnacprData } = useQuery(
    [QK.dnacpr, selectedId],
    () => getDnacprById(selectedId),
    { enabled: !!selectedId }
  );

  const editMutation = useMutation(({ id, editData }) => updateDnacpr(id, editData), {
    onSuccess: () => {
      enqueueSnackbar('DNACPR updated successfully!', { variant: 'success' });
      handleClose();
      refecthDnacpr();
    },
    onError: (error) => {
      enqueueSnackbar(`Error: ${error.message}`, { variant: 'error' });
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const editData = new FormData();
    if (e.target.dnacprFile.files.length > 0) {
      editData.append("cert", e.target.dnacprFile.files[0]);
    }
    editData.append("start_date", e.target.startDate.value);
    editData.append("client", clientId);

    if (selectedId) {
      editMutation.mutate({ id: selectedId, editData });
    } 
  };

  return (
    <Dialog 
      open={show} 
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)'
        }
      }}
    >
      <DialogTitle sx={{ 
        bgcolor: 'primary.main', 
        color: 'white',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        py: 2
      }}>
        <Typography variant="h6">Edit DNACPR</Typography>
        <IconButton
          onClick={() => {
            handleClose();
            setSelectedFile(null);
          }} sx={{ color: 'white' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ py: 3 }}>
        <Box component="form" onSubmit={handleSubmit}>
          <Stack spacing={3}>
            {/* File Upload Section */}
            <Paper elevation={0} sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                DNACPR Certificate
              </Typography>
              
              {DnacprData?.cert && (
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  mb: 2,
                  p: 2,
                  bgcolor: 'action.hover',
                  borderRadius: 1
                }}>
                  <FileIcon color="primary" sx={{ mr: 2 }} />
                  <Box sx={{ flexGrow: 1 }}>
                    <Link 
                      href={DnacprData.cert} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      sx={{ fontWeight: 500 }}
                    >
                      View Current Certificate
                    </Link>
                    <Typography variant="body2" color="text.secondary">
                      Upload new file to replace
                    </Typography>
                  </Box>
                </Box>
              )}
              <FormControl fullWidth>
                <Button
                  component="label"
                  variant="outlined"
                  color="primary"
                  startIcon={<UploadIcon />}
                  fullWidth
                  sx={{ py: 1.5, borderRadius: 1 }}
                >
                  {selectedFile ? selectedFile.name : 'Upload New Certificate'}
                  <input
                    type="file"
                    name="dnacprFile"
                    hidden
                    onChange={(e) => {
                      if (e.target.files.length > 0) {
                        setSelectedFile(e.target.files[0]);
                      }
                    }}
                  />
                </Button>
                {selectedFile && (
                  <Typography variant="body2" sx={{ mt: 1, color: 'text.primary' }}>
                    Selected: {selectedFile.name}
                  </Typography>
                )}
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                  PDF, JPG, or PNG (Max 5MB)
                </Typography>
              </FormControl>
            </Paper>

            {/* Date Section */}
            <Paper elevation={0} sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                DNACPR Details
              </Typography>
              
              <FormControl fullWidth>
                <TextField
                  label="Start Date"
                  type="date"
                  name="startDate"
                  defaultValue={DnacprData?.start_date}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  InputProps={{
                    startAdornment: <CalendarIcon color="action" sx={{ mr: 1 }} />,
                  }}
                />
              </FormControl>
            </Paper>
          </Stack>

          <Divider sx={{ my: 3 }} />

          <DialogActions sx={{ px: 0 }}>
            <Button 
              onClick={() => {
                handleClose();
                setSelectedFile(null);
              }}
              variant="outlined"
              sx={{ 
                px: 3,
                py: 1,
                borderRadius: 1,
                textTransform: 'none'
              }}
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              variant="contained" 
              color="primary"
              disabled={editMutation.isLoading}
              sx={{ 
                px: 3,
                py: 1,
                borderRadius: 1,
                textTransform: 'none',
                boxShadow: 'none'
              }}
            >
              {editMutation.isLoading ? (
                <>
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                  Saving...
                </>
              ) : 'Save Changes'}
            </Button>
          </DialogActions>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default DnacprEditModal;